import React from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch, useSelector} from 'react-redux';
import {WeatherService} from '../api';
import {Location} from '../api/types';
import {
  selectFavoritePlacesWithoutForecastModels,
  setLocationForecastModels,
} from '../store';
import {CommonRootState} from '../store/types';
import {isModelListFetchedAt, recordModelListFetch} from './modelListFetches';
import useApiClient from './useApiClient';
import {USER_LOCATION_HASH} from '../utils/MyWeather/constants';

const NO_PLACES: Location[] = [];

/** A place at its point: asked once per point, not once per hash. */
const askKey = (place: Location) =>
  `${place.hash}@${place.coordinates.latitude},${place.coordinates.longitude}`;

/**
 * Whether the list held for a place is its own. For the GPS entry, only a
 * list fetched for its current point this session (`modelListFetches`, as
 * `useLocationForecast` decides).
 */
const hasOwnList = (place: Location, known: Record<string, unknown>) =>
  place.hash in known &&
  (place.hash !== USER_LOCATION_HASH ||
    isModelListFetchedAt(
      place.hash,
      place.coordinates.latitude,
      place.coordinates.longitude,
    ));

/** How many lists are asked at once, by one run. */
const CONCURRENCY = 4;

/**
 * Each place's list still out (asked, or queued to be), by `askKey`, for the
 * session rather than the mount: a list or settings screen remounted while
 * its places are out (the My weather search crossing 3 letters) waits for
 * those rather than asking them again (FE #418). An entry leaves when its
 * fetch ends: a fetched place is no longer missing (its list is stored), and
 * a failed one is asked again by the next mount.
 */
const outstanding = new Map<string, Promise<void>>();

/** Forget the lists out (tests). */
export const clearPlaceModelListAsks = () => {
  outstanding.clear();
};

/**
 * Fetches the forecast model list of every favourite place that has none,
 * and of any other place handed in (`also`) that has none.
 *
 * The forecast-model settings screens say where each pinned model is used,
 * and wait until every point's list is known (`selectCoverageStations`). A
 * place's own screen is what fetched its list, so a place never opened kept
 * them waiting for good: on the webapp, which has no place screens, as soon
 * as the account held one place (backend#266).
 *
 * Four places at a time (`CONCURRENCY`), each asked at most once while the
 * screen is mounted (the GPS entry once per point), and never twice at once
 * across mounts (`outstanding`): a failed one stays unknown rather than
 * being read as "covered by nothing", until a later mount asks it again.
 *
 * Returns the hashes still waiting for their list (not fetched or failed
 * yet), so a row can wait for the model its screen will show.
 */
const useMissingPlaceForecastModels = ({
  also = NO_PLACES,
}: {
  /**
   * More places to cover than the favourites: the My weather list's rows
   * (recent places, the Current location once located), so a row asks
   * `point/now` for the model its screen shows (FE #381).
   */
  also?: Location[];
} = {}) => {
  const {fetch} = useApiClient();
  const dispatch = useDispatch();
  const missing = useSelector<CommonRootState, Location[]>((state) => {
    const favorites = selectFavoritePlacesWithoutForecastModels(state);
    const known = state.location.forecastModels;
    const seen = new Set(favorites.map((place) => place.hash));
    const more = also.filter((place) => {
      if (hasOwnList(place, known) || seen.has(place.hash)) {
        return false;
      }
      seen.add(place.hash);
      return true;
    });
    return more.length > 0 ? [...favorites, ...more] : favorites;
  }, isEqual);
  const asked = React.useRef(new Set<string>());
  const running = React.useRef(false);
  // Bumped when a run ends, so places that became missing meanwhile are
  // picked up by the next one.
  const [runs, setRuns] = React.useState(0);
  // Places whose fetch ended, fetched or failed (by `askKey`).
  const [settled, setSettled] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );

  React.useEffect(() => {
    if (running.current) {
      return;
    }
    const todo = missing.filter((place) => !asked.current.has(askKey(place)));
    if (todo.length === 0) {
      return;
    }
    running.current = true;
    // Places out already, from a run of an earlier mount: waited for, not
    // asked again.
    const joined: Promise<void>[] = [];
    const queue: {place: Location; done: () => void}[] = [];
    for (const place of todo) {
      const key = askKey(place);
      asked.current.add(key);
      const settle = () => setSettled((previous) => new Set(previous).add(key));
      const out = outstanding.get(key);
      if (out) {
        joined.push(out.then(settle));
        continue;
      }
      let release = () => {};
      const ours = new Promise<void>((resolve) => {
        release = resolve;
      });
      outstanding.set(key, ours);
      queue.push({
        place,
        done: () => {
          if (outstanding.get(key) === ours) {
            outstanding.delete(key);
          }
          release();
          settle();
        },
      });
    }
    const ask = async ({place, done}: (typeof queue)[number]) => {
      const {latitude, longitude} = place.coordinates;
      try {
        const forecastModels = await fetch(
          WeatherService.locationWeatherForecastModelList(latitude, longitude),
          {silent: true, ignore404: true},
        );
        dispatch(setLocationForecastModels({hash: place.hash, forecastModels}));
        // Only once the list is stored: recorded before, a pending or
        // failed fetch made the list held (a moved Current location's old
        // one) read as this point's, and let the place's screen skip its
        // own fetch for a minute. `asked` already stops a repeat.
        recordModelListFetch(place.hash, latitude, longitude);
      } catch {
        // Stays unknown; asked again the next time the screen opens.
      }
      done();
    };
    // `CONCURRENCY` lanes, each taking the next place as its last lands.
    const lane = async () => {
      for (let next = queue.shift(); next; next = queue.shift()) {
        await ask(next);
      }
    };
    const run = () =>
      Promise.all([
        ...joined,
        ...Array.from({length: Math.min(CONCURRENCY, queue.length)}, lane),
      ]);
    // Not tied to this effect's cleanup: the run's own dispatches re-run the
    // effect, which would otherwise cancel the bump for good. React 19 does
    // not warn on a state update after unmount.
    run().finally(() => {
      running.current = false;
      setRuns((n) => n + 1);
    });
  }, [missing, runs, fetch, dispatch]);

  // Still waiting for a list: not fetched, and not failed, yet.
  return React.useMemo(
    () =>
      missing
        .filter((place) => !settled.has(askKey(place)))
        .map((place) => place.hash),
    [missing, settled],
  );
};

export default useMissingPlaceForecastModels;
